/**
 * Client-side CSV parsing with PapaParse.
 * Handles header aliases (case-insensitive) for expected product fields.
 */

import Papa from "papaparse";

/** Expected canonical headers and their allowed aliases (case-insensitive) */
const HEADER_ALIASES: Record<string, string[]> = {
  id: ["id"],
  sku: ["sku"],
  title: ["title", "name"],
  description: ["description", "desc"],
  price: ["price"],
  currency: ["currency"],
  category: ["category"],
  imageUrl: ["image", "imageurl", "image_url", "img"],
  inventory: ["inventory", "stock", "qty", "quantity"],
  tags: ["tags", "tag"],
  size: [
    "size",
    "unit",
    "weight",
    "volume",
    // "Size / Unit", "Pack size", etc. (slashes/spaces stripped — see normalizeCsvHeaderKey)
    "sizeunit",
    "packsize",
    "productsize",
    "itemsize",
    "netweight",
    "netsize",
    "servingsize",
  ],
};

/** Collapse spaces, slashes, dashes so "Size / Unit" and "size_unit" both match size aliases. */
export function normalizeCsvHeaderKey(header: string): string {
  return header.trim().toLowerCase().replace(/[\s/\\_.-]+/g, "");
}

/** Map a raw header string to canonical key, or null if not recognized */
function mapHeader(header: string): string | null {
  const normalized = normalizeCsvHeaderKey(header);
  for (const [canonical, aliases] of Object.entries(HEADER_ALIASES)) {
    if (aliases.some((a) => normalized === normalizeCsvHeaderKey(a))) return canonical;
  }
  return null;
}

/** Auto-map raw CSV header row to canonical field → column index (last duplicate wins). */
export function autoMapCsvHeaders(rawHeaders: string[]): Record<string, number> {
  const mapping: Record<string, number> = {};
  rawHeaders.forEach((h, i) => {
    const canonical = mapHeader(String(h ?? ""));
    if (canonical) mapping[canonical] = i;
  });
  return mapping;
}

export type ParseResult =
  | { ok: true; rows: Record<string, string>[]; headers: string[] }
  | { ok: false; error: string };

/**
 * Parse CSV string and return rows as objects keyed by *canonical* headers.
 * Unrecognized headers are dropped. Recognized headers are mapped from aliases.
 */
export function parseCSV(csvString: string): ParseResult {
  const result = Papa.parse<string[]>(csvString, {
    header: false,
    skipEmptyLines: true,
  });

  if (result.errors.length > 0) {
    const first = result.errors[0];
    return {
      ok: false,
      error: first.message || "Invalid CSV format",
    };
  }

  const rows = result.data;
  if (!rows?.length) {
    return { ok: false, error: "CSV is empty" };
  }

  const rawHeaders = rows[0];
  const canonicalToIndex: Record<string, number> = {};
  const headers: string[] = [];

  rawHeaders.forEach((h, i) => {
    const canonical = mapHeader(String(h ?? ""));
    if (canonical) {
      canonicalToIndex[canonical] = i;
      headers.push(canonical);
    }
  });

  const mappedRows: Record<string, string>[] = [];
  for (let r = 1; r < rows.length; r++) {
    const row = rows[r];
    const obj: Record<string, string> = {};
    for (const [canonical, idx] of Object.entries(canonicalToIndex)) {
      const val = row?.[idx];
      if (val !== undefined && val !== null) obj[canonical] = String(val).trim();
    }
    mappedRows.push(obj);
  }

  return { ok: true, rows: mappedRows, headers };
}

/** Raw parse result for column-mapping UI: first row as headers, rest as row arrays */
export type RawParseResult =
  | { ok: true; rawHeaders: string[]; rawRows: string[][] }
  | { ok: false; error: string };

export function parseCSVRaw(csvString: string): RawParseResult {
  const result = Papa.parse<string[]>(csvString, {
    header: false,
    skipEmptyLines: true,
  });

  if (result.errors.length > 0) {
    return {
      ok: false,
      error: result.errors[0].message || "Invalid CSV format",
    };
  }

  const rows = result.data;
  if (!rows?.length) {
    return { ok: false, error: "CSV is empty" };
  }

  const rawHeaders = rows[0].map((h) => String(h ?? "").trim());
  const rawRows = rows.slice(1).map((row) => row.map((c) => String(c ?? "").trim()));
  return { ok: true, rawHeaders, rawRows };
}

/** Canonical field names we support for mapping */
export const CANONICAL_FIELDS = [
  "id",
  "sku",
  "title",
  "description",
  "price",
  "currency",
  "category",
  "imageUrl",
  "inventory",
  "tags",
  "size",
] as const;

/** Build mapped rows from raw rows using column index mapping: canonicalField -> columnIndex */
export function buildMappedRows(
  rawRows: string[][],
  mapping: Record<string, number>
): Record<string, string>[] {
  return rawRows.map((row) => {
    const obj: Record<string, string> = {};
    for (const [field, idx] of Object.entries(mapping)) {
      if (idx >= 0 && row[idx] !== undefined) obj[field] = row[idx];
    }
    return obj;
  });
}
